import PptxGenJS from 'pptxgenjs';
import { SupportedWidgetRenderType, widgetPptType } from './pptWidgetConfig';
import { SlideContent } from '../../../../hooks/usePowerPointExport';
import {
  PPTMultiQuestionSummaryRenderer,
  PPTsummaryBarRenderer,
  PPTsurveyQuestionRenderer,
} from './pptWidgetRenderer';
import {
  PPTGenerateParameters,
  PPTGenerateSplitSlideParameters,
  PPTPosition,
} from '../../../../models/pptx.model';
import {
  CHART_HEIGHT,
  CHART_HEIGHT_NO_QUESTION,
  CHART_HEIGHT_NO_QUESTION_NO_SUMMARY_BAR,
  CHART_START_Y,
  CHART_WIDTH,
  COLOR_SECONDARY,
  COLORS_ACCENT,
  FONT_FAMILY,
  FONT_SIZE_DATA_LABEL,
  FONT_SIZE_H4,
  FONT_SIZE_H6,
  FONT_SIZE_P,
  FOOTER_HEIGHT,
  PAGE_START_X,
  PAGE_START_Y,
  QUESTION_SUBHEADER_HEIGHT,
  SLIDE_HEIGHT,
  SUMMARY_BAR_HEIGHT,
  SUMMARY_BAR_START_X,
  SUMMARY_BAR_START_Y,
} from './constants';
import { getGeographyRegionColor } from '../../widget-utils/widgetUtils';
import { castToType, formatAppDate, isReportViewMode } from '../../../../hooks/utils';
import { QuestionModel } from '../../../../models/dataset.model';
import { format } from 'date-fns';
import { SurveyCitation } from '../../../../models/elsie-ai.model';
import { PPT_DATA_PROCESSOR_MAP, PPT_RENDER_MAP } from './mapper';
import { Aggregate, SingleAggregateOption } from 'common/models/widget.model';
import { WidgetExportModel } from 'common/models/export.model';
import { widgetHasData } from 'common/utils/widgetHasData';

const generateAggregatesFromConfig = (
  summaryBar: WidgetExportModel,
  isAlignment?: boolean
): Aggregate[] => {
  // Get the aggregates from the config
  const configAggregates: string[] = summaryBar[0].widget.request.data.aggregates[0].aggregates.map(
    (aggregate: SingleAggregateOption) => aggregate.sourceField
  );

  const responseAggregates = summaryBar[0].response.aggregates;

  if (isAlignment) {
    // If it's an alignment summary, tag latest alignment aggregate on the end
    configAggregates.push('AverageAlignmentScore');
    configAggregates.push('TotalProfile');
    configAggregates.push('TotalResponse');
    configAggregates.push('FieldTeamId');
  }

  return configAggregates.map((configAggregate) => {
    return responseAggregates.find(
      (aggregate: Aggregate) => aggregate.name === configAggregate
    ) as Aggregate;
  });
};
const SLIDE_W = 10;
const gutter = 0.05;

const leftX = 0;
const leftW = 6.2;
const rightX = leftX + leftW + gutter;
const rightW = SLIDE_W - rightX;
const SAFE_BOTTOM_PAD = 0.05;

/**
 * Calculates the height and vertical position for a box anchored to the slide footer without overlapping the top limit.
 */
function getAnchoredBoxUp(topLimit: number, wantedH: number) {
  const bottomLimit = SLIDE_HEIGHT - FOOTER_HEIGHT - SAFE_BOTTOM_PAD;
  const maxH = Math.max(0, bottomLimit - topLimit);
  const h = Math.min(wantedH, maxH);
  const y = bottomLimit - h;

  return { y, h };
}

export const PPTgenerateEmptyWidgetSlide = ({
  widget,
  summaryBar = [],
  question,
  appliedFilter,
  universalFilters,
  isReport,
  isAlignment,
}: PPTGenerateParameters): SlideContent[] => {
  const widgetFilter = {
    startDate: widget.defaultFilters?.startDate ?? appliedFilter.startDate,
    endDate: widget.defaultFilters?.endDate ?? appliedFilter.endDate,
    reduxFilter: widget.defaultFilters?.filters ?? appliedFilter.reduxFilter,
  };

  const chartPosition: PPTPosition = {};
  if (!question && summaryBar.length === 0) {
    chartPosition['y'] = PAGE_START_Y + 0.2;
    chartPosition['h'] = CHART_HEIGHT_NO_QUESTION_NO_SUMMARY_BAR;
  }
  if (!question && summaryBar.length > 0) {
    chartPosition['y'] = SUMMARY_BAR_START_Y + 0.1;
    chartPosition['h'] = CHART_HEIGHT_NO_QUESTION;
  }

  return [
    {
      title: widget.title,
      content: (slide: PptxGenJS.Slide, pptx: PptxGenJS) => {
        if (question) {
          PPTsurveyQuestionRenderer({ slide, question, pptx });
        }

        if (summaryBar.length > 0 && !isReport) {
          PPTsummaryBarRenderer({
            slide,
            summaryData: generateAggregatesFromConfig(summaryBar, isAlignment),
            pptx,
            isAlignment: isAlignment,
            position: !question ? { x: SUMMARY_BAR_START_X, y: PAGE_START_Y + 0.2 } : undefined,
          });
        }

        const coords = PPTgetChartCoords(chartPosition);
        slide.addText('No Data Available', {
          x: coords.x,
          y: coords.y,
          w: coords.w,
          h: coords.h,
          align: 'center',
          valign: 'middle',
          fontSize: FONT_SIZE_H4,
          fontFace: FONT_FAMILY,
          color: COLORS_ACCENT,
        });

        if (isReport) {
          const noteText = PPTformatNotesFromFilter(
            widget?.title,
            widgetFilter.startDate,
            widgetFilter.endDate,
            widgetFilter.reduxFilter
          );
          slide.addNotes(noteText);
          const universalNoteText = PPTformatNotesFromUniversalFilter(
            widgetFilter.startDate,
            widgetFilter.endDate,
            castToType<Record<string, string>>(universalFilters?.filters)
          );
          slide.addNotes(universalNoteText);
        }
      },
    },
  ];
};

/**
 * The ElsieAI export widget is configured as dataType 'list' (its request
 * fetches the kwelloIds to summarise), but WizardSummary replaces its
 * response with { summary, citationsData } before the slide is generated.
 * widgetHasData()'s list-shape check would therefore always report "no
 * data" — gate the Elsie slide on the summary text instead.
 */
const pptSlideHasData = (params: PPTGenerateParameters): boolean => {
  if (widgetPptType[params.widget?.type] === SupportedWidgetRenderType.Elsie) {
    const elsieData = params.data as { summary?: { chatResponse?: string } } | undefined;
    return !!elsieData?.summary?.chatResponse;
  }
  return widgetHasData(params.widget, params.data);
};

export const PPTgenerateWidgetSlide = (params: PPTGenerateParameters): SlideContent[] => {
  if (!pptSlideHasData(params)) {
    return PPTgenerateEmptyWidgetSlide(params);
  }

  const {
    widget,
    data,
    chartOptions,
    summaryBar = [],
    question,
    appliedFilter,
    universalFilters,
    isReport,
    isAlignment,
  } = params;
  const renderType = widgetPptType[widget.type];
  const dataProcessor = PPT_DATA_PROCESSOR_MAP[renderType];
  const pptRenderer = PPT_RENDER_MAP[renderType];
  const widgetFilter = {
    startDate: widget.defaultFilters?.startDate ?? appliedFilter.startDate,
    endDate: widget.defaultFilters?.endDate ?? appliedFilter.endDate,
    reduxFilter: widget.defaultFilters?.filters ?? appliedFilter.reduxFilter,
  };
  if (!dataProcessor) {
    console.warn(`Unsupported widget data processor: ${renderType}`);
  }
  if (!pptRenderer) {
    throw new Error(`Unsupported widget renderer: ${renderType}`);
  }

  const chartPosition: PPTPosition = {};

  if (!question && summaryBar.length === 0) {
    chartPosition['y'] = PAGE_START_Y + 0.2;
    chartPosition['h'] = CHART_HEIGHT_NO_QUESTION_NO_SUMMARY_BAR;
  }

  if (!question && summaryBar.length > 0) {
    chartPosition['y'] = SUMMARY_BAR_START_Y + 0.1;
    chartPosition['h'] = CHART_HEIGHT_NO_QUESTION;
  }

  const result: SlideContent[] = [
    {
      title: widget.title,
      content: (slide: PptxGenJS.Slide, pptx: PptxGenJS) => {
        const renderData = dataProcessor
          ? dataProcessor({ data, chartOptions, widget, appliedFilter: widgetFilter })
          : data;
        if (question) {
          PPTsurveyQuestionRenderer({ slide, question, pptx });
        }

        if (summaryBar.length > 0 && renderType !== SupportedWidgetRenderType.Elsie && !isReport) {
          PPTsummaryBarRenderer({
            slide,
            summaryData: generateAggregatesFromConfig(summaryBar, isAlignment),
            pptx,
            isAlignment: isAlignment,
            position: !question ? { x: SUMMARY_BAR_START_X, y: PAGE_START_Y + 0.2 } : undefined,
          });
        }

        // --- Apply to renderer ---
        pptRenderer({
          data: renderData,
          slide,
          rawData: data,
          pptx,
          chartOptions,
          widget,
          position: chartPosition,
        });

        if (isReport) {
          const noteText = PPTformatNotesFromFilter(
            widget?.title,
            widgetFilter.startDate,
            widgetFilter.endDate,
            widgetFilter.reduxFilter
          );
          slide.addNotes(noteText);
          const universalNoteText = PPTformatNotesFromUniversalFilter(
            widgetFilter.startDate,
            widgetFilter.endDate,
            castToType<Record<string, string>>(universalFilters?.filters)
          );
          slide.addNotes(universalNoteText);
        }
      },
    },
  ];
  return result;
};

export const PPTgenerateSplitSlide = ({
  leftWidget,
  rightWidget,
  summaryBar,
  leftWidgetData,
  rightWidgetData,
  question,
  projectName,
  appliedFilter,
  isAlignment,
}: PPTGenerateSplitSlideParameters): SlideContent => {
  const leftRenderType = widgetPptType[leftWidget.type];
  const rightRenderType = widgetPptType[rightWidget.type];
  const leftDataProcessor = PPT_DATA_PROCESSOR_MAP[leftRenderType];
  const rightDataProcessor = PPT_DATA_PROCESSOR_MAP[rightRenderType];
  const leftPptRenderer = PPT_RENDER_MAP[leftRenderType];
  const rightPptRenderer = PPT_RENDER_MAP[rightRenderType];
  const topLimit = CHART_START_Y - 0.4;
  const wantedH = CHART_HEIGHT + 0.8; // for the dounat chart
  const rightBox = getAnchoredBoxUp(topLimit, wantedH);
  if (!leftDataProcessor) {
    throw new Error(`Unsupported left widget data processor: ${leftRenderType}`);
  }
  if (!leftPptRenderer) {
    throw new Error(`Unsupported left widget renderer: ${rightRenderType}`);
  }
  if (!rightDataProcessor) {
    throw new Error(`Unsupported right widget data processor: ${leftRenderType}`);
  }
  if (!rightPptRenderer) {
    throw new Error(`Unsupported right widget renderer: ${rightRenderType}`);
  }
  const leftRenderData = leftDataProcessor
    ? leftDataProcessor({
        data: leftWidgetData,
        widget: leftWidget,
        appliedFilter,
      })
    : [];
  const rightRenderData = rightDataProcessor
    ? rightDataProcessor({
        data: question.isAlignment ? deepCloneSwapXAndTooltip(rightWidgetData) : rightWidgetData,
        widget: rightWidget,
        appliedFilter,
      })
    : [];
  const result: SlideContent = {
    title: `${projectName} | Question Analysis`,
    content: (slide: PptxGenJS.Slide, pptx: PptxGenJS) => {
      if (question) {
        PPTsurveyQuestionRenderer({
          slide,
          question: castToType<QuestionModel>({
            name: question.questionText,
            parent: {
              name: question.surveyTitle,
            },
          }),
          pptx,
        });
      }
      if (summaryBar) {
        PPTMultiQuestionSummaryRenderer({
          slide,
          summaryData: generateAggregatesFromConfig(summaryBar, isAlignment),
          pptx,
          question,
          startDate: appliedFilter.startDate,
          endDate: appliedFilter.endDate,
          isAlignment,
        });
      }
      leftPptRenderer({
        data: leftRenderData,
        slide,
        rawData: leftWidgetData,
        pptx,
        widget: leftWidget,
        overrideRenderOptions: {
          x: leftX,
          w: leftW,
          legendPos: 't',
          showValue: true,
          title: 'Response volume',
          showTitle: true,
          titleFontFace: FONT_FAMILY,
          titleFontSize: FONT_SIZE_P,
        },
      });
      rightPptRenderer({
        data: rightRenderData,
        slide,
        rawData: rightWidgetData,
        pptx,
        widget: rightWidget,
        overrideRenderOptions: {
          x: rightX,
          w: rightW,
          y: rightBox.y,
          h: rightBox.h,
          legendPos: 'r',
          legendFontSize: FONT_SIZE_DATA_LABEL,
          title: 'Total Response Volume',
          showTitle: true,
          titleFontFace: FONT_FAMILY,
          titleFontSize: FONT_SIZE_P,
          holeSize: 45,
        },
      });
    },
  };
  return result;
};

export function PPTrenderTable(
  data: Record<string, number>,
  slide: PptxGenJS.Slide,
  title: string,
  fillColor: string, // hex string like "#29B2FF"
  x: number,
  y: number,
  colSpan: number = 2 // Default to 2 columns
) {
  const tableData: PptxGenJS.TableRow[] = [
    [
      {
        text: title,
        options: {
          fontSize: 12,
          align: 'center',
          colspan: colSpan,
          fill: { color: fillColor },
          fontFace: FONT_FAMILY,
          color: 'FFFFFF',
        },
      },
    ],
    ...Object.entries(data).map(([key, value]) => [
      { text: key, options: { fontSize: 12 } },
      { text: value.toString(), options: { fontSize: 12 } },
    ]),
  ];

  slide.addTable(tableData, {
    x: x,
    y: y,
    w: colSpan * 0.5,
    colW: [1, 1],
    fontFace: FONT_FAMILY,
    fill: { color: 'FFFFFF' },
    border: { pt: 1, color: '000000' },
  });
}

export const PPTrenderSeparator = (slide: PptxGenJS.Slide, x: number, y: number) => {
  slide.addText(
    [
      {
        text: '|',
        options: {
          fontSize: FONT_SIZE_H6,
          color: COLOR_SECONDARY,
          fontFace: FONT_FAMILY,
        },
      },
    ],
    {
      x: x,
      y: y,
      w: 0.1,
      h: 0.1,
      align: 'center',
    }
  );
};

export function PPTformatNotesFromFilter(
  slideTitle: string,
  startDate: string,
  endDate: string,
  filter: Record<string, any>
): string {
  return PPTformatNotesFilter(`Slide Title: ${slideTitle}`, startDate, endDate, filter);
}

export function PPTformatNotesFromUniversalFilter(
  startDate: string,
  endDate: string,
  filter: Record<string, any>
): string {
  return PPTformatNotesFilter(`\n\nUniversal filters:`, startDate, endDate, filter);
}

function PPTformatNotesFilter(
  introText: string,
  startDate: string,
  endDate: string,
  filter: Record<string, any>
): string {
  const formattedStart = format(new Date(startDate), 'dd-MMM-yy');
  const formattedEnd = format(new Date(endDate), 'dd-MMM-yy');

  let notes = `${introText}\n\nDate Range: ${formattedStart} → ${formattedEnd}\n\n`;

  const entries = Object.entries(filter);

  if (!entries.length) {
    notes += 'No filters applied.';
  } else {
    notes += 'Applied Filters:\n';
    notes += entries
      .map(([key, value]) => {
        if (value?.label && Array.isArray(value.value)) {
          const names = value.value.map((v: { name: string }) => v.name).join(', ');
          return `  - ${value.label}: ${names}`;
        }
        return `  - ${key}: ${JSON.stringify(value)}`;
      })
      .join('\n');
  }

  return notes;
}

export const PPTmarkdownToText = (root: Record<string, any>): PptxGenJS.TextProps[] => {
  const nodeIterator = (child: Record<string, any>): PptxGenJS.TextProps => {
    let result: PptxGenJS.TextProps = { text: '', options: { fontSize: FONT_SIZE_P } };
    if (child.type === 'strong') {
      result = {
        text: child.children[0]?.value,
        options: {
          bold: true,
          fontSize: FONT_SIZE_P,
          fontFace: FONT_FAMILY,
          color: COLORS_ACCENT,
        },
      };
    }
    if (child.type === 'emphasis') {
      result = {
        text: child.children[0]?.value,
        options: {
          italic: true,
          fontSize: FONT_SIZE_P,
          fontFace: FONT_FAMILY,
          color: COLORS_ACCENT,
        },
      };
    }
    if (child.type === 'link') {
      result = {
        text: `[${child.children[0]?.value}]`,
        options: {
          fontSize: FONT_SIZE_P,
          fontFace: FONT_FAMILY,
          color: COLORS_ACCENT,
        },
      };
    }
    if (child.type === 'text') {
      result = {
        text: child.value,
        options: { fontSize: FONT_SIZE_P, fontFace: FONT_FAMILY, color: COLORS_ACCENT },
      };
    }
    return result;
  };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return castToType<PptxGenJS.TextProps[]>(root.children.map(nodeIterator));
};

function deepCloneSwapXAndTooltip(node: any): any {
  if (Array.isArray(node)) {
    return node.map(deepCloneSwapXAndTooltip);
  }

  if (typeof node === 'object' && node !== null) {
    const clone: any = {};
    for (const [key, value] of Object.entries(node)) {
      // Handle children first
      if (key === 'groupedList' || key === 'data') {
        clone[key] = deepCloneSwapXAndTooltip(value);
      } else {
        clone[key] = value;
      }
    }

    // Swap x and tooltip only if both exist
    if ('x' in clone && 'tooltip' in clone) {
      const temp = clone.x;
      clone.x = clone.tooltip;
      clone.tooltip = temp;
      delete clone.alignmentLabel;
    }

    return clone;
  }

  return node;
}

export function PPTprocessElsieCitationNotes(
  ordinals: Record<string, string>,
  citationsData: SurveyCitation[]
) {
  let result = '\n\n';
  Object.keys(ordinals).forEach((key) => {
    const ordinalId = parseInt(ordinals[key]);
    const citation = citationsData.find((c) => c.responseId === ordinalId);
    if (citation) {
      result += `Citation ${key}\nProfile: ${citation.profile}\nDate: ${formatAppDate(citation.date)}\nQuestion: ${citation.question}\nResponse Details: ${citation.answer}\nTeam Member: ${citation.fieldTeam}\n------------------------------------------------------------------------------------\n\n`;
    }
  });
  return result;
}

export function PPTgetChartCoords(position?: PPTPosition): PptxGenJS.PositionProps {
  const isElsie = window.location.href.includes('elsie');
  if (isReportViewMode() || isElsie) {
    return {
      x: PAGE_START_X,
      y: PAGE_START_Y,
      w: CHART_WIDTH,
      h: CHART_HEIGHT + SUMMARY_BAR_HEIGHT + QUESTION_SUBHEADER_HEIGHT + 0.4,
    };
  }
  return {
    x: position?.x ?? PAGE_START_X,
    y: position?.y ?? CHART_START_Y,
    w: CHART_WIDTH,
    h: position?.h ?? CHART_HEIGHT,
  };
}

export const PPTgenerateGeographyMapXml = async (data: { [key: string]: number }) => {
  const filtered = Object.entries(data).filter(([_, v]) => v > 0);

  const countries = filtered.map(([k]) => k);
  const values = filtered.map(([_, v]) => v);

  const globalMin = Math.min(...values.filter((v) => v > 0));
  const globalMax = Math.max(...values);
  const ptCount = countries.length;
  const bin = await fetch('/bin/geo.bin');
  const binaryBuffer = await bin.text();

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cx:chartSpace xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
    xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
    xmlns:cx="http://schemas.microsoft.com/office/drawing/2014/chartex">
    <cx:chartData>
        <cx:data id="0">
            <cx:strDim type="cat">
                <cx:f>Sheet1!$A$2:$A$${ptCount + 1}</cx:f>
                <cx:lvl ptCount="${ptCount}">
                    ${countries.map((country, idx) => `<cx:pt idx="${idx}">${country}</cx:pt>`).join('')}
                </cx:lvl>
            </cx:strDim>
            <cx:numDim type="colorVal">
                <cx:f>Sheet1!$B$2:$B$${ptCount + 1}</cx:f>
                <cx:lvl ptCount="${ptCount}" formatCode="General">
                    ${values.map((value, idx) => `<cx:pt idx="${idx}">${value}</cx:pt>`).join('')}
                </cx:lvl>
            </cx:numDim>
        </cx:data>
    </cx:chartData>
    <cx:chart>
        <cx:plotArea>
            <cx:plotAreaRegion>
                <cx:series layoutId="regionMap" uniqueId="{1A64C0EB-1133-BA41-B64B-B035B90FDAA2}">
                    <cx:tx>
                        <cx:txData>
                            <cx:f>Sheet1!$B$1</cx:f>
                            <cx:v>Responses</cx:v>
                        </cx:txData>
                    </cx:tx>
                      ${values
                        .map((value, idx) => {
                          const { hex, alpha } = getGeographyRegionColor(
                            value,
                            globalMin,
                            globalMax
                          );
                          return `<cx:dataPt idx="${idx}"><cx:spPr><a:solidFill><a:srgbClr val="${hex}"><a:alpha val="${Math.round(alpha * 100000)}" /></a:srgbClr></a:solidFill></cx:spPr></cx:dataPt>`;
                        })
                        .join('')}

                    <cx:dataId val="0" />
                    <cx:layoutPr>
                        <cx:geography cultureLanguage="en-GB" cultureRegion="BG">
                            <cx:geoCache provider="{E9337A44-BEBE-4D9F-B70C-5C5E7DAFC167}">
                                <cx:binary>${binaryBuffer}</cx:binary>
                            </cx:geoCache>
                        </cx:geography>
                    </cx:layoutPr>
                </cx:series>
            </cx:plotAreaRegion>
        </cx:plotArea>
        <cx:legend pos="r" align="min" overlay="0" />
    </cx:chart>
</cx:chartSpace>`;
};

export function forceTextColorForPpt(svg: string, color = '#000000') {
  // normalize casing for safety
  svg = svg.replace(/viewbox=/i, 'viewBox=');

  // Replace any explicit 'currentColor' anywhere
  svg = svg.replace(/currentColor/gi, color);

  // Remove color on ancestors that could leak (optional but helpful)
  svg = svg.replace(/\scolor=["'][^"']*["']/gi, '');

  // Rewrite every <text> to have explicit fill, no stroke/paint-order
  svg = svg.replace(/<text\b([^>]*)>/gi, (m, attrs) => {
    let a = attrs;

    // Strip conflicting attributes
    a = a
      .replace(/\sfill=["'][^"']*["']/gi, '')
      .replace(/\sfill-opacity=["'][^"']*["']/gi, '')
      .replace(/\sstroke=["'][^"']*["']/gi, '')
      .replace(/\sstroke-width=["'][^"']*["']/gi, '')
      .replace(/\spaint-order=["'][^"']*["']/gi, '');

    // Clean inline style= of fill/color/stroke that PPT might misread
    a = a.replace(/\sstyle=["'][^"']*["']/gi, (styleAttr: string) => {
      const cleaned = styleAttr
        .replace(/fill\s*:\s*[^;]+;?/gi, '')
        .replace(/color\s*:\s*[^;]+;?/gi, '')
        .replace(/stroke\s*:\s*[^;]+;?/gi, '')
        .replace(/stroke-width\s*:\s*[^;]+;?/gi, '')
        .replace(/paint-order\s*:\s*[^;]+;?/gi, '');
      // drop empty style
      return /style=["']\s*["']/.test(cleaned) ? '' : cleaned;
    });

    // Ensure explicit fill
    return `<text${a} fill="${color}" fill-opacity="1">`;
  });

  return svg;
}
